/*
Event-Slide des Kalender-Sliders. Eigene Komponente, weil sie einen Ref auf ihre Wurzel
braucht: darüber trackt useContentImpression die Content-Impression, sobald der Slide zu
80 % sichtbar ist (siehe src/utils/matomo.ts).

Die Styles kommen weiter aus EventsCalendarView.module.scss — Markup und Layout des Slides
sind unverändert.
*/

'use client';

import { CategoryLink } from '@/sanity/fetchCategories';
import { EventCard } from '@/sanity/fetchEvents';
import { URL_PATH_EVENT } from '@/shared/constants';
import {
  MATOMO_ACTIONS,
  MATOMO_CATEGORIES,
  MATOMO_CONTENT_INTERACTIONS,
  MATOMO_CONTENT_KINDS,
  MATOMO_NAMES,
  buildContentPiece,
  trackMatomoEvent,
} from '@/utils/matomo';
import { useContentImpression } from '@/hooks/useContentImpression';
import { ContentBlockProvider } from '../base/ContentTracking';
import HIHImage from '../base/HIHImage';
import HIHLink from '../base/Link';
import { CategoryTag } from '../base/Tag';
import ActionFavorite from '../action/favorite/ActionFavorite';
import EventFacts from './EventFacts';
import styles from './EventsCalendarView.module.scss';

export default function EventsCalendarViewEventSlide({
  event,
  position,
  selectedDate,
  serverNow,
  locationOnClick,
  ticketLinkOnClick,
}: {
  event: EventCard;
  /** 1-basierte Slide-Position, wird als Wert des bestehenden Matomo-Events gesendet. */
  position: number;
  selectedDate: Date;
  serverNow: Date;
  locationOnClick: () => void;
  ticketLinkOnClick: () => void;
}) {
  const eventPath = `/${URL_PATH_EVENT}/${event.slug.current}`;

  const { ref, block, trackInteraction } = useContentImpression({
    contentPiece: buildContentPiece(MATOMO_CONTENT_KINDS.EVENT, event.slug.current),
    contentTarget: eventPath,
  });

  const handleOpenClick = () =>
    trackInteraction(MATOMO_CONTENT_INTERACTIONS.DETAILSEITE_GEOEFFNET);

  // Das Ereignis hängt unverändert nur am Bild-Link, damit die bestehende Reihe im
  // Ereignisse-Report ihre Bedeutung behält. Die Content Interaction zählt beide Links.
  const handleImageClick = () => {
    trackMatomoEvent(
      MATOMO_CATEGORIES.INTERACTIONS,
      MATOMO_ACTIONS.EVENT_TIP_CLICK,
      MATOMO_NAMES.CALENDAR_EVENT_TIP_CLICK,
      position,
    );
    handleOpenClick();
  };

  return (
    <ContentBlockProvider block={block}>
      <div style={{ position: 'relative' }} ref={ref}>
        <HIHLink href={eventPath} onClick={handleImageClick}>
          <div className={styles.slideImageWrapper}>
            <HIHImage
              className={styles.slideImage}
              image={event.coverImage}
              fill
              sizes="(min-width: 1024px) 33vw, 80vw"
              priority
            />
          </div>
        </HIHLink>
        <div className={styles.slideContent}>
          <div>
            <div className={styles.categoryTags}>
              {event.categories?.slice(0, 2).map((category: CategoryLink) => (
                <CategoryTag key={category._id} category={category} />
              ))}
            </div>
            <HIHLink href={eventPath} onClick={handleOpenClick}>
              <h2 className={styles.slideTitle}>{event.title}</h2>
            </HIHLink>
          </div>
          <EventFacts
            event={event}
            currentDate={selectedDate}
            today={serverNow}
            className={styles.eventFactsSpacing}
            locationOnClick={locationOnClick}
            ticketLinkOnClick={ticketLinkOnClick}
          />
        </div>
        <ActionFavorite
          id={event._id}
          className={styles.favoriteButton}
          bookmarkType="Event"
        />
      </div>
    </ContentBlockProvider>
  );
}
